import request from './index'
import { getToken } from '../utils/auth'
import { fetchEventSource } from '@microsoft/fetch-event-source'
import { API_BASE_URL } from '../constants/env'

/**
 * SSE 连接管理器 v3
 *
 * v2 → v3 修复：
 * 1. 【关键】fetchEventSource 内部重连会复用原始 URL（lastMsgId 不变）
 *    → 改为 onerror/onclose 中手动调用 _startFetch 重建连接，URL 动态生成
 * 2. 【关键】心跳超时 abort 后，fetchEventSource 不会自动重连
 *    → 在心跳超时回调中手动触发重连
 * 3. 连接状态精确管理：connecting 时跳过重复连接请求
 * 4. lastMsgId 持久化到 sessionStorage，页面刷新不丢失
 */
class SseManager {
  constructor() {
    this.clientId = null
    this.controller = null
    this.callbacks = null
    this.state = 'idle'             // idle | connecting | connected
    this.manualClose = false        // 主动关闭不重连
    this.lastHeartbeat = 0
    this.heartbeatTimer = null
    this.reconnectAttempts = 0
    this.reconnectTimer = null      // setTimeout id
    this._loadPersistedMsgId()
  }

  // ==================== 公共方法 ====================

  /**
   * 建立 SSE 连接
   */
  connect(callbacks) {
    if (this.state !== 'idle') {
      console.log('[SSE] 状态:', this.state, '，跳过连接')
      return
    }

    const token = getToken()
    if (!token) {
      console.error('[SSE] 无 token，跳过连接')
      return
    }

    this.callbacks = callbacks
    this.initClientId()
    this.manualClose = false
    this.reconnectAttempts = 0

    this._doConnect(token)
  }

  /**
   * 断开连接（不重连）
   */
  disconnect() {
    this.manualClose = true
    this._clearReconnectTimer()
    this._cleanup()
    this.state = 'idle'
    console.log('[SSE] 已断开')
  }

  /**
   * 确保 SSE 已连接（切回前台时调用）
   * 检查心跳超时 + 连接状态，必要时重连
   */
  ensureConnected() {
    if (this.manualClose) return false
    if (!getToken()) return false
    if (this.state === 'connected') {
      // 已连接，但检查心跳是否超时（切后台后浏览器可能静默杀死连接）
      // 与 _startHeartbeatCheck 的45s阈值保持一致
      if (this.lastHeartbeat > 0 && Date.now() - this.lastHeartbeat > 45000) {
        console.warn('[SSE] ensureConnected: 心跳超时45s，强制重连')
        this._stopHeartbeatCheck()
        this.state = 'idle'
        if (this.controller) {
          this.controller.abort()
          this.controller = null
        }
      } else {
        return true  // 连接正常
      }
    }

    // 不是 connected 或心跳超时，强制重连
    console.log('[SSE] ensureConnected: 当前状态=' + this.state + '，触发重连')
    this._clearReconnectTimer()
    this._cleanup()
    this.state = 'idle'
    this.reconnectAttempts = 0

    const token = getToken()
    if (token && this.callbacks) {
      this._doConnect(token)
      return true
    }
    return false
  }

  /**
   * 获取状态
   */
  getStatus() {
    return {
      clientId: this.clientId,
      state: this.state,
      lastMsgId: this.lastMsgId,
      lastHeartbeat: this.lastHeartbeat,
      reconnectAttempts: this.reconnectAttempts
    }
  }

  // ==================== 核心连接 ====================

  /**
   * 执行一次 fetchEventSource 请求（每次调用都生成最新 URL）
   */
  _doConnect(token) {
    // 标记为"正在重建"，防止 cleanup 后的 onerror/onclose 再次调度重连
    this._stopHeartbeatCheck()
    this._clearReconnectTimer()
    if (this.controller) {
      this._reconnecting = true
      this.controller.abort()
      this.controller = null
    }
    this.state = 'connecting'
    const newController = new AbortController()
    this.controller = newController
    const self = this

    // 🔑 每次连接都用最新的 lastMsgId 构建 URL
    const url = `${API_BASE_URL}/sse/subscribe?clientId=${this.clientId}&lastMsgId=${this.lastMsgId}`

    console.log('[SSE] 连接中:', this.clientId, 'lastMsgId:', this.lastMsgId)

    fetchEventSource(url, {
      headers: { Authorization: `Bearer ${token}` },
      signal: newController.signal,

      // HTTP 连接建立
      async onopen(response) {
        // 旧连接残留回调，忽略
        if (self.controller !== newController) return
        if (!response.ok) {
          if (response.status >= 400 && response.status < 500) {
            self.manualClose = true
            console.warn('[SSE] 认证失败:', response.status, '，停止重连')
            throw new Error(`[SSE] ${response.status}`)
          }
          throw new Error(`[SSE] ${response.status}`)
        }
        console.log('[SSE] 连接成功, lastMsgId=' + self.lastMsgId)
        self.state = 'connected'
        self._reconnecting = false
        self.reconnectAttempts = 0
        self._startHeartbeatCheck()
        self.callbacks?.onopen?.()
      },

      // SSE 消息
      onmessage(event) {
        // 🔑 旧连接残留消息保护：abort 后 fetchEventSource 可能还在
        //    事件循环里投递缓冲消息，必须检查 controller 是否已被替换
        if (self.controller !== newController) return

        // 心跳
        if (event.event === 'heartbeat') {
          self.lastHeartbeat = Date.now()
          return
        }

        // 连接确认
        if (event.event === 'connect') {
          self.lastHeartbeat = Date.now()
          return
        }

        // 业务消息
        if (event.data) {
          try {
            const data = JSON.parse(event.data)
            if (data.msgId !== undefined && data.msgId > self.lastMsgId) {
              self.lastMsgId = data.msgId
              self._persistMsgId()
            }
            self.callbacks?.onmessage?.(data)
          } catch (e) {
            console.error('[SSE] 解析失败:', e)
          }
        }
      },

      // 错误
      onerror(err) {
        if (self.controller !== newController) throw err
        self._stopHeartbeatCheck()
        self.state = 'idle'

        if (self._reconnecting) {
          self._reconnecting = false
          throw err
        }

        if (self.manualClose) {
          console.log('[SSE] 主动关闭')
          throw err
        }

        if (err.message && /^\[SSE\] [4]\d\d$/.test(err.message)) {
          console.warn('[SSE] 认证失败，停止重连')
          throw err
        }

        console.log('[SSE] 连接错误，准备重连（lastMsgId=' + self.lastMsgId + '）')
        self._scheduleReconnect()
        throw err
      },

      // 服务端关闭
      onclose() {
        if (self.controller !== newController) return
        self._stopHeartbeatCheck()
        self.state = 'idle'

        if (self._reconnecting) {
          self._reconnecting = false
          return
        }

        if (self.manualClose) {
          console.log('[SSE] 连接关闭')
          return
        }

        console.log('[SSE] 连接意外关闭，准备重连（lastMsgId=' + self.lastMsgId + '）')
        self._scheduleReconnect()
      }
    }).catch(err => {
      if (self._reconnecting) {
        self._reconnecting = false
        return
      }
      if (err?.name !== 'AbortError' && !self.manualClose) {
        console.warn('[SSE] fetchEventSource 未捕获异常:', err?.message || err)
      }
    })
  }

  // ==================== 重连控制 ====================

  /**
   * 调度延迟重连（单一入口，不会重复调度）
   */
  _scheduleReconnect(delay) {
    if (this.manualClose) return
    if (this.reconnectTimer) return  // 已有重连在排队

    this.reconnectAttempts++
    const actualDelay = delay || this._calcReconnectDelay()

    console.log(`[SSE] ${actualDelay}ms 后重连（第${this.reconnectAttempts}次，lastMsgId=${this.lastMsgId}）`)

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null
      if (this.manualClose) return
      if (this.state !== 'idle') return

      const token = getToken()
      if (!token) {
        console.log('[SSE] 无 token，停止重连')
        return
      }
      if (!this.callbacks) return

      // 🔑 重新调用 _doConnect，URL 中的 lastMsgId 是最新值
      this._doConnect(token)
    }, actualDelay)
  }

  _clearReconnectTimer() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
  }

  /**
   * 抖动指数退避
   */
  _calcReconnectDelay() {
    const base = Math.min(1000 * Math.pow(2, this.reconnectAttempts - 1), 30000)
    const jitter = base * (0.7 + Math.random() * 0.6)
    return Math.round(jitter)
  }

  // ==================== 心跳检测 ====================

  /**
   * 启动心跳检测（90秒无心跳 → abort → 手动触发重连）
   */
  _startHeartbeatCheck() {
    this._stopHeartbeatCheck()
    this.lastHeartbeat = Date.now()
    this.heartbeatTimer = setInterval(() => {
      if (this.state !== 'connected') return
      if (Date.now() - this.lastHeartbeat > 45000) {
        console.warn('[SSE] 心跳超时45s，abort触发重连（lastMsgId=' + this.lastMsgId + '）')
        this.manualClose = false
        this._stopHeartbeatCheck()
        this.state = 'idle'
        // abort 当前连接 → 触发 onerror/onclose → 但我们已经 state=idle 并调度重连
        // 为防止 onerror/onclose 再次调度重连，先清理
        if (this.controller) {
          this.controller.abort()
          this.controller = null
        }
        this._scheduleReconnect(2000)
      }
    }, 15000)  // 每15秒检查一次（更快检测到超时）
  }

  _stopHeartbeatCheck() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer)
      this.heartbeatTimer = null
    }
  }

  // ==================== lastMsgId 持久化 ====================

  _loadPersistedMsgId() {
    this.clientId = sessionStorage.getItem('sse_client_id')
    const saved = sessionStorage.getItem('sse_last_msg_id')
    this.lastMsgId = saved !== null ? parseInt(saved, 10) : -1
  }

  _persistMsgId() {
    sessionStorage.setItem('sse_last_msg_id', String(this.lastMsgId))
  }

  // ==================== 初始化 ====================

  initClientId() {
    if (this.clientId) return
    this.clientId = sessionStorage.getItem('sse_client_id')
    if (!this.clientId) {
      this.clientId = 'sse-' + Date.now().toString(36) + '-' + Math.random().toString(36).substr(2, 9)
      sessionStorage.setItem('sse_client_id', this.clientId)
      console.log('[SSE] clientId:', this.clientId)
    }
  }

  // ==================== 清理 ====================

  _cleanup() {
    this._stopHeartbeatCheck()
    if (this.controller) {
      this.controller.abort()
      this.controller = null
    }
  }
}

// 单例
const sseManager = new SseManager()

/**
 * 订阅 SSE
 */
export function subscribeSse(callbacks) {
  sseManager.connect(callbacks)
  return {
    close: () => sseManager.disconnect(),
    getStatus: () => sseManager.getStatus(),
    ensureConnected: () => sseManager.ensureConnected()
  }
}

// ==================== 消息相关 ====================

export function getUnreadCount() { return request.get('/task-message/unread-count') }
export function getMessages(params) { return request.get('/task-message/page', { params }) }
export function markAsRead(id) { return request.put(`/task-message/${id}/read`) }
export function markAllAsRead() { return request.put('/task-message/read-all') }

// ==================== 异步任务相关 ====================

export function submitAsyncTask(data) { return request.post('/async-task', data) }
export function getTaskDetail(id) { return request.get(`/async-task/${id}`) }
export function getTaskList(params) { return request.get('/async-task/page', { params }) }
export function getProcessingTasks() { return request.get('/async-task/processing') }
